// Slug generation lives in seo.js so the app, the sitemap generator and the
// prerenderer cannot drift apart on what an episode URL looks like.
import { slugify } from './seo.js';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

export const PALETTE = ['#FF6B6B', '#845EC2', '#FF7B00', '#FFC75F', '#FF2D78', '#4D8FFF'];

export function podcastAccent(uuid) {
  const sum = (uuid ?? '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return PALETTE[sum % PALETTE.length];
}

let _cache = null;
let _colorMap = null;

function buildColorMap(podcasts) {
  const COLS = 3;
  const assigned = [];
  const map = {};
  for (let i = 0; i < podcasts.length; i++) {
    const uuid = podcasts[i].podcast_uuid;
    const preferred = podcastAccent(uuid);
    const forbidden = new Set();
    if (i % COLS !== 0) forbidden.add(assigned[i - 1]);                              // left
    if (i % COLS === 0 && i > 0) forbidden.add(assigned[i - 1]);                    // row-wrap
    if (i >= COLS) forbidden.add(assigned[i - COLS]);                               // top
    if (i >= COLS && i % COLS > 0) forbidden.add(assigned[i - COLS - 1]);           // top-left diagonal
    if (i >= COLS && i % COLS < COLS - 1) forbidden.add(assigned[i - COLS + 1]);    // top-right diagonal
    const color = !forbidden.has(preferred)
      ? preferred
      : (PALETTE.find(c => !forbidden.has(c)) ?? preferred);
    assigned.push(color);
    map[uuid] = color;
  }
  _colorMap = map;
  return map;
}

export function getPodcastColor(uuid) {
  const map = _colorMap ?? (_cache ? buildColorMap(_cache.podcasts ?? []) : null);
  return map?.[uuid] ?? podcastAccent(uuid);
}

/**
 * Prime the module cache from Node, where `fetch('/data.json')` cannot resolve
 * a root-relative URL. Called once by scripts/prerender.js before rendering.
 *
 * Also builds the colour map eagerly. `buildColorMap` assigns accents by
 * position in the podcast array, so whichever call happens first fixes the
 * mapping for the whole process. Doing it here guarantees the prerendered
 * markup and the hydrated client agree on every accent colour.
 *
 * @param {object} data - parsed contents of public/data.json
 */
export function seedCache(data) {
  _cache = data;
  buildColorMap(data?.podcasts ?? []);
}

/**
 * Read the small, route-specific data slice that scripts/prerender.js inlined
 * into this page's HTML as `window.__TM_PAGE__`.
 *
 * `renderToString` never runs effects, so a page that loads its data in a
 * `useEffect` would prerender as an empty skeleton. Pages instead seed their
 * initial state from this payload, which makes the first render synchronous on
 * the server AND on the client, so hydration matches with no flash. It also
 * means first paint no longer waits on the 2 MB data.json.
 *
 * The payload describes the entry URL only, so it is returned solely for the
 * route it was built for. Any client-side navigation elsewhere gets null and
 * falls back to the normal async path. It is deliberately NOT deleted after
 * reading: React may invoke a state initialiser more than once (StrictMode,
 * hydration retries), and every one of those must see the same value.
 *
 * @param {string} routeKey - identifies the route, e.g. `episode:jre:2530-x`
 * @returns {object|null} the slice, or null if this is not the entry page
 */
export function pageData(routeKey) {
  const payload = typeof globalThis !== 'undefined' ? globalThis.__TM_PAGE__ : null;
  if (!payload || payload.route !== routeKey) return null;
  return payload;
}

// Every colour on a speaking bar is mixed from that podcast's own accent, so
// two podcasts never share a guest colour and a single show never changes
// palette between its older episodes and its audited ones. Hosts walk DOWN the
// accent (darkening towards the card background), the first guest sits below
// the darkest host, and any further guest walks UP (lightening towards white).
// The two directions are what keeps host and guest apart once a panel has
// three or four voices.

// The lone/first guest. Both bars call this, so an episode looks the same
// before and after it gains a speaker_breakdown.
// The trailing 90%/black step is the block sitting 10% darker than the mix the
// bar used to draw. The label under it is lightened separately, so the two move
// in opposite directions on purpose.
export const guestBarColor = accent =>
  `color-mix(in srgb, color-mix(in srgb, ${accent} 28%, #1e1e2a) 90%, #000000)`;

// Grey for the guest's text under the bar, defined once in main.css so the
// two-speaker bar (which styles its label in CSS) and the multi bar (which gets
// it from here) cannot drift apart.
const GUEST_LABEL_COLOR = 'var(--bar-guest-label)';

// Second and third guests. Darker than the first guest would fall into the card
// background, so extra guests lighten instead — still the podcast's hue, and
// nowhere near any host step.
const EXTRA_GUEST_SHADES = [
  accent => `color-mix(in srgb, ${accent} 55%, #ffffff)`,
  accent => `color-mix(in srgb, ${accent} 30%, #ffffff)`,
];

// Host shades spread evenly from the full accent down to HOST_FLOOR. The floor
// stops even a four-host panel from putting a host on top of the guest colour
// or of the surface behind the bar.
const HOST_FLOOR = 52;
const hostShades = count => {
  if (count <= 1) return [accent => accent];
  const step = (100 - HOST_FLOOR) / (count - 1);
  return Array.from({ length: count }, (_, i) =>
    i === 0
      ? accent => accent
      : accent => `color-mix(in srgb, ${accent} ${(100 - i * step).toFixed(1)}%, #14141f)`);
};

/**
 * Build the per-speaker bar segments for a multi-host episode.
 * Named hosts are shown by FIRST NAME (unless SPEAKER_LABEL_OVERRIDES says
 * otherwise, for panels where two hosts share a first name) and take shades of
 * the accent; a lone guest is labelled simply "Guest". See the naming rule below
 * for when a speaker is named at all. Every colour comes from this podcast's own
 * accent (see the ramp above), so no colour is shared with another podcast.
 * Order follows the stored breakdown (host1, host2, host3, guest).
 * @param {Array<{name:string, role:string, pct:number}>} breakdown
 * @param {string} accent
 * @param {string} [podcastUuid] - only for first-name label overrides
 * @returns {Array<{name:string, pct:number, color:string, labelColor?:string}>}
 *   `labelColor` overrides the segment colour for the text under the bar; it is
 *   only set where the segment colour would be too dark to read.
 */
// First names are not unique on every panel: All-In has both a David Sacks and
// a David Friedberg, which renders as two identical "David" segments. These
// overrides give the colliding host the label the show itself uses.
const SPEAKER_LABEL_OVERRIDES = {
  'all-in': { 'David Sacks': 'Sacks' },
};

export function buildSpeakerBars(breakdown, accent, podcastUuid) {
  const labelOverrides = SPEAKER_LABEL_OVERRIDES[podcastUuid] ?? {};
  const hostCount = (breakdown ?? []).filter(s => s.role !== 'guest').length;
  // Naming rule (same one the question labels use): a host is named once there
  // is more than one of them, a guest once there is more than one of them. The
  // lone host of a one-host/one-guest interview therefore stays generic "Host",
  // matching that episode's question labels; a lone host with no guest, or with
  // two-plus guests, is still named because every voice on the bar is named
  // there.
  const guestCount = (breakdown ?? []).filter(s => s.role === 'guest').length;
  const nameHosts  = hostCount > 1 || guestCount !== 1;
  const nameGuests = guestCount > 1;
  const shades = hostShades(hostCount).map(fn => fn(accent));
  const guestShades = [guestBarColor(accent), ...EXTRA_GUEST_SHADES.map(fn => fn(accent))];
  let hostIdx = 0;
  let guestIdx = 0;
  return (breakdown ?? []).map(s => {
    const isGuest = s.role === 'guest';
    const named = labelOverrides[s.name] ?? (s.name.split(' ')[0] || s.name);
    return {
      name: isGuest
        ? (nameGuests ? named : 'Guest')
        : (nameHosts ? named : 'Host'),
      pct: s.pct,
      color: isGuest
        ? (guestShades[guestIdx++] ?? guestShades[guestShades.length - 1])
        : (shades[hostIdx++] ?? accent),
      // The label under the bar is small text on a dark card, so it cannot
      // simply reuse the segment colour: the guest shades sit below the host
      // floor and go unreadable at that size. Hosts are bright enough to keep
      // their own colour, which is what ties a name to its block; guests fall
      // back to the muted grey the two-speaker bar has always used for them.
      labelColor: isGuest ? GUEST_LABEL_COLOR : undefined,
    };
  });
}

/**
 * The two hosts of a hosts-only pair, or null for every other episode shape.
 * This is the one shape where `host_questions` / `guest_questions` stop naming
 * anybody, so both readers of that rule ask this question the same way.
 */
function hostsOnlyPair(episode) {
  const bd = episode?.speaker_breakdown;
  if (!bd?.length) return null;
  const hosts = bd.filter(s => s.role === 'host');
  const guests = bd.filter(s => s.role === 'guest');
  return hosts.length === 2 && guests.length === 0 ? hosts : null;
}

/**
 * Whether an episode's two question counts can be shown as a pair at all.
 *
 * They are keyed to a host side and a guest side, so the pair only says
 * something when the episode has both sides. A solo episode has nobody to
 * compare against. A hosts-only PANEL of three or more has no guest side: its
 * `host_questions` is the whole panel's total and `guest_questions` is leftover
 * noise, so naming one "Guest questions" states a guest that was never there.
 *
 * The hosts-only PAIR is the exception that stays showable: `questionSplit`
 * re-keys those two numbers to the two hosts by name whenever the breakdown
 * carries per-speaker counts, and returns nulls when it cannot.
 * @param {object} episode
 * @returns {boolean}
 */
export function showsQuestionSplit(episode) {
  const bd = episode?.speaker_breakdown;
  if (!bd?.length) return episode?.guest_pct > 0;
  if (bd.length < 2) return false;
  const hosts = bd.filter(s => s.role === 'host').length;
  return bd.length > hosts || hosts === 2;
}

/**
 * The two question counts to show, in the order the labels name them.
 *
 * `host_questions` / `guest_questions` are keyed to a host/guest split, and both
 * names come off the episode record — never off the podcast config, which says
 * nothing about who spoke in one episode.
 *
 * A two-host episode has no host/guest split, so those two numbers no longer say
 * which host each belongs to. The per-speaker `questions` on the breakdown entry
 * does say, so it wins whenever it is there. When it is not, the counts cannot be
 * attributed to a name at all, and showing them beside two host names would be a
 * coin flip presented as fact, so show nothing.
 * @param {object} episode
 * @returns {{hostQuestions: number|null, guestQuestions: number|null}}
 */
export function questionSplit(episode) {
  const stored = {
    hostQuestions: episode?.host_questions ?? null,
    guestQuestions: episode?.guest_questions ?? null,
  };
  // Only a hosts-only pair loses the host/guest keying. Every other shape still
  // has exactly one side the stored counts can belong to.
  const hosts = hostsOnlyPair(episode);
  if (!hosts) return stored;
  if (hosts[0].questions == null || hosts[1].questions == null) {
    return { hostQuestions: null, guestQuestions: null };
  }
  return { hostQuestions: hosts[0].questions, guestQuestions: hosts[1].questions };
}

/**
 * How many questions the HOST SIDE of an episode asked: every speaker with the
 * host role, added up. That is what "host questions" already means everywhere
 * else in the data: on a four-host All-In episode the stored `host_questions` is
 * the whole panel's total, not one panellist's.
 *
 * The hosts-only PAIR is the one shape that breaks that. There, `host_questions`
 * holds one of the two hosts and `guest_questions` the other, so reading
 * `host_questions` alone silently ranks half a conversation against a full one.
 * `questionSplit` already separates those two numbers per host, so the host-side
 * total is simply both of them.
 *
 * Returns null when the total cannot be established (a hosts-only pair with no
 * per-speaker counts). Null keeps the episode OUT of any ranking rather than
 * ranking it on a number that does not measure what the ranking claims.
 * @param {object} episode
 * @returns {number|null}
 */
export function hostQuestionCount(episode) {
  const { hostQuestions, guestQuestions } = questionSplit(episode);
  if (!hostsOnlyPair(episode)) return hostQuestions;
  if (hostQuestions == null || guestQuestions == null) return null;
  return hostQuestions + guestQuestions;
}

// Stats whose stored field does not mean the same thing on every episode shape,
// and the function that works out the honest value instead.
const DERIVED_METRICS = {
  host_questions: hostQuestionCount,
};

/**
 * Value of a sortable/rankable stat for one episode.
 *
 * Most stats are plain fields on the record. A few are derived, because the
 * stored field does not mean the same thing on every episode shape. Sorting and
 * ranking both go through here so a column, its sort option and its displayed
 * number can never disagree.
 * @param {object} episode
 * @param {string} key - field name, e.g. 'host_pct' or 'host_questions'
 */
export function episodeMetric(episode, key) {
  const derive = DERIVED_METRICS[key];
  return derive ? derive(episode) : episode?.[key];
}

async function _load() {
  if (_cache) return _cache;
  const res = await fetch(`${BASE}/data.json`);
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  _cache = await res.json();
  return _cache;
}

/**
 * The whole dataset. Used by the editorial pages, which compute their own
 * aggregates via content/stats.js rather than going through the per-podcast
 * helpers below.
 */
export function loadData() {
  return _load();
}

/** advertisers.json, fetched only by the page that needs it. */
let _advertisers = null;
export async function loadAdvertisers() {
  if (_advertisers) return _advertisers;
  const res = await fetch(`${BASE}/advertisers.json`);
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  _advertisers = await res.json();
  return _advertisers;
}

export { slugify };

/**
 * Fetch the full list of podcasts.
 * @returns {Promise<{ podcasts: Podcast[] }>}
 */
export async function fetchPodcasts() {
  const d = await _load();
  return { podcasts: d.podcasts ?? [] };
}

/**
 * Fetch a single podcast by its UUID / slug.
 * podcast_uuid IS the slug (already kebab-case).
 * @param {string} slug
 * @returns {Promise<Podcast>}
 */
export async function fetchPodcast(slug) {
  const d = await _load();
  return (d.podcasts ?? []).find(p => p.podcast_uuid === slug) ?? null;
}

/**
 * Fetch a single episode by its title slug within a podcast.
 * @param {string} podcastSlug
 * @param {string} episodeSlug
 * @returns {Promise<Episode>}
 */
export async function fetchEpisodeBySlug(podcastSlug, episodeSlug) {
  const d = await _load();
  const eps = d.episodes?.[podcastSlug] ?? [];
  // BE-007: warn if more than one episode maps to the same slug
  const matches = eps.filter(e => slugify(e.title) === episodeSlug);
  if (matches.length > 1) {
    console.warn(`[api] Slug collision: ${matches.length} episodes in "${podcastSlug}" share slug "${episodeSlug}"`);
  }
  return matches[0] ?? null;
}

/**
 * Fetch episodes for a podcast with optional filters (client-side).
 * @param {string} podcastId
 * @param {Object} params
 * @param {string} [params.sort_by]
 * @param {string} [params.order]
 * @param {string} [params.status]
 * @param {number} [params.limit]
 * @param {number} [params.cursor]      - Offset into the filtered list (for infinite scroll)
 * @param {string} [params.search]
 * @param {string[]} [params.keywords]  - Active keyword pills; episode must match each
 * @returns {Promise<{ episodes: Episode[], next_cursor: number|null, meta: object }>}
 */
export async function fetchEpisodes(podcastId, params = {}) {
  const d = await _load();
  let episodes = (d.episodes?.[podcastId] ?? []).slice();

  // Status filter
  const status = params.status ?? 'all';
  if (status !== 'all') {
    const target = status === 'complete' ? 'complete' : 'pending_transcript';
    episodes = episodes.filter(e => e.status === target);
  }

  // Keyword search across title, title-keywords, and transcript top_words
  if (params.search) {
    const terms = params.search.toLowerCase().split(/\s+/).filter(Boolean);
    episodes = episodes.filter(e => {
      const topWords = (e.top_words ?? []).map(w => w.word ?? '').join(' ');
      const hay = (e.title ?? '').toLowerCase()
        + ' ' + (e.keywords ?? []).join(' ')
        + ' ' + topWords;
      return terms.every(t => hay.includes(t));
    });
  }

  // Active keyword pills — episode must match every selected keyword in
  // title, title-keywords, or transcript top_words
  if (params.keywords?.length > 0) {
    episodes = episodes.filter(e => {
      const titleLower = (e.title ?? '').toLowerCase();
      const titleKws = new Set((e.keywords ?? []).map(k => k.toLowerCase()));
      const topWordSet = new Set((e.top_words ?? []).slice(0, 5).map(w => (w.word ?? '').toLowerCase()));
      return params.keywords.every(kw => {
        const k = kw.toLowerCase();
        return topWordSet.has(k) || titleKws.has(k) || titleLower.includes(k);
      });
    });
  }

  // Sort
  const sortBy = params.sort_by ?? 'published_at';
  const asc = (params.order ?? 'desc') === 'asc';
  episodes.sort((a, b) => {
    // Derived stats (see episodeMetric) can be null on episodes where the stored
    // field does not mean what the sort option says; those sort to the bottom.
    const av = episodeMetric(a, sortBy) ?? '';
    const bv = episodeMetric(b, sortBy) ?? '';
    if (av < bv) return asc ? -1 : 1;
    if (av > bv) return asc ? 1 : -1;
    return 0;
  });

  // BE-009: cap page size to prevent accidental huge fetches
  const limit = Math.min(Number(params.limit ?? 20), 200);
  // Cursor = numeric offset into the filtered+sorted list. Drives infinite scroll:
  // each page returns the offset of the next one, or null when the list is exhausted.
  const offset = Math.max(0, Number(params.cursor ?? 0) || 0);
  const page = episodes.slice(offset, offset + limit);
  const nextOffset = offset + limit;
  const next_cursor = nextOffset < episodes.length ? nextOffset : null;

  return {
    episodes: page,
    next_cursor,
    meta: { shown: page.length, total: episodes.length, offset },
  };
}

/**
 * Aggregate transcript top_words across all episodes of a podcast.
 * Sums per-episode counts and returns the top N word strings.
 * @param {string} podcastId
 * @param {number} [limit=10]
 * @returns {Promise<string[]>}
 */
export async function fetchPodcastTopWords(podcastId, limit = 10) {
  const d = await _load();
  const episodes = d.episodes?.[podcastId] ?? [];
  const totals = new Map();
  for (const ep of episodes) {
    for (const entry of (ep.top_words ?? [])) {
      const w = entry?.word;
      const c = entry?.count ?? 0;
      if (!w) continue;
      totals.set(w, (totals.get(w) ?? 0) + 1);
    }
  }
  return [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([word]) => word);
}

/**
 * Fetch all episodes across all podcasts as a flat array.
 * @returns {Promise<Episode[]>}
 */
export async function fetchAllEpisodes() {
  const d = await _load();
  const all = [];
  for (const eps of Object.values(d.episodes ?? {})) {
    all.push(...eps);
  }
  return all;
}

/**
 * Fetch a single episode by its UUID (legacy) or by title slug.
 * @param {string} episodeId  - UUID or title slug
 * @param {string} podcastId  - podcast UUID / slug
 * @returns {Promise<Episode>}
 */
export async function fetchEpisode(episodeId, podcastId) {
  const d = await _load();
  const eps = d.episodes?.[podcastId] ?? [];
  // Try exact UUID match first, then slug match
  return (
    eps.find(e => e.episode_uuid === episodeId) ??
    eps.find(e => slugify(e.title) === episodeId) ??
    null
  );
}

/**
 * Build the URL path to an episode detail page.
 * @param {string} podcastSlug
 * @param {Episode} episode
 * @returns {string}
 */
export function episodeUrl(podcastSlug, episode) {
  return `/podcast/${podcastSlug}/episode/${slugify(episode.title)}`;
}

/**
 * Returns the top N words by episode count across all episodes in every podcast.
 * Each word scores 1 point per episode it appears in (length-agnostic).
 * @param {number} [limit=5]
 * @returns {Promise<string[]>}
 */
export async function fetchGlobalTopWords(limit = 5) {
  const d = await _load();
  const counts = new Map();
  for (const eps of Object.values(d.episodes ?? {})) {
    for (const ep of eps) {
      for (const entry of (ep.top_words ?? [])) {
        const w = entry?.word;
        if (!w) continue;
        counts.set(w, (counts.get(w) ?? 0) + 1);
      }
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([word]) => word);
}

/**
 * Returns all episodes (from all podcasts) where `word` is in their top_words[:5].
 * Each episode object includes a `podcast_name` field.
 * @param {string} word
 * @returns {Promise<Episode[]>}
 */
export async function fetchEpisodesByKeyword(word) {
  const d = await _load();
  const podcastMap = Object.fromEntries((d.podcasts ?? []).map(p => [p.podcast_uuid, p]));
  const needle = word.toLowerCase();
  const results = [];
  for (const [podcastId, eps] of Object.entries(d.episodes ?? {})) {
    const podcast = podcastMap[podcastId];
    for (const ep of eps) {
      const topFive = (ep.top_words ?? []).slice(0, 5).map(w => (w.word ?? '').toLowerCase());
      if (topFive.includes(needle)) {
        results.push({ ...ep, podcast_name: podcast?.name ?? podcastId });
      }
    }
  }
  return results;
}

/**
 * Searches ALL episodes across all podcasts.
 * Matches if query word(s) appear in: episode title, top_words[:5], or podcast name.
 * Returns episodes with podcast_name attached, sorted by relevance (title match first,
 * then top_words match).
 * @param {string} query
 * @returns {Promise<Episode[]>}
 */
export async function fetchEpisodesBySearch(query) {
  const d = await _load();
  const podcastMap = Object.fromEntries((d.podcasts ?? []).map(p => [p.podcast_uuid, p]));
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];

  const titleMatches = [];
  const topWordMatches = [];

  for (const [podcastId, eps] of Object.entries(d.episodes ?? {})) {
    const podcast = podcastMap[podcastId];
    const podcastNameLower = (podcast?.name ?? '').toLowerCase();
    for (const ep of eps) {
      const titleLower = (ep.title ?? '').toLowerCase();
      const topFive = (ep.top_words ?? []).slice(0, 5).map(w => (w.word ?? '').toLowerCase()).join(' ');
      const inTitle = terms.every(t => titleLower.includes(t) || podcastNameLower.includes(t));
      const inTopWords = terms.every(t => topFive.includes(t));
      if (inTitle) {
        titleMatches.push({ ...ep, podcast_name: podcast?.name ?? podcastId });
      } else if (inTopWords) {
        topWordMatches.push({ ...ep, podcast_name: podcast?.name ?? podcastId });
      }
    }
  }

  return [...titleMatches, ...topWordMatches];
}

/**
 * Returns the top `limit` episodes globally sorted by `metric` field (desc),
 * with podcast_name attached.
 * @param {string} metric
 * @param {number} [limit=10]
 * @returns {Promise<Episode[]>}
 */
export async function fetchTopEpisodes(metric, limit = 10, filter) {
  const d = await _load();
  const podcastMap = Object.fromEntries((d.podcasts ?? []).map(p => [p.podcast_uuid, p]));
  const all = [];
  for (const [podcastId, eps] of Object.entries(d.episodes ?? {})) {
    const podcast = podcastMap[podcastId];
    for (const ep of eps) {
      // A null metric means this episode has no honest value for this ranking
      // (see episodeMetric), so it is left out rather than ranked on a guess.
      if (episodeMetric(ep, metric) != null && (!filter || filter(ep))) {
        all.push({ ...ep, podcast_name: podcast?.name ?? podcastId });
      }
    }
  }
  all.sort((a, b) => (episodeMetric(b, metric) ?? 0) - (episodeMetric(a, metric) ?? 0));
  return all.slice(0, limit);
}
